// src/app/layout/SideBar.js

import React, { useEffect, useId, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import AreaNavigation from "./AreaNavigation";
import ReportLinks from "./ReportLinks";
import { isMapsRoute } from "../../shared/navigation/mapsRoutes";
import SidebarLayout from "./SidebarLayout";
import glastonburyLogo from "../../images/glastonbury-river-data-logo.png";

const SEEC_SELECTOR_INPUT_CLASS =
  "w-full px-3 py-2 rounded-md text-sm leading-5 font-normal bg-white text-gray-900 border border-gray-300 placeholder:text-sm focus:outline-none focus:ring-2 focus:ring-blue-300";
const SEEC_SELECTOR_ROW_CLASS =
  "w-full text-left px-3 py-2 text-sm leading-5 font-medium tracking-normal border-b border-blue-900 last:border-b-0 transition rounded-none";

function SeecSelectorSection({
  title,
  placeholder,
  items,
  selectedItem,
  onSelectItem,
  emptyMessage,
  maxListHeightClass = "max-h-72",
}) {
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(!selectedItem);
  const contentId = useId();
  useEffect(() => {
    if (selectedItem) {
      setCollapsed(false);
      setSearch("");
    }
  }, [selectedItem]);

  const filteredItems = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((item) => terms.every((term) => String(item).toLowerCase().includes(term)));
  }, [items, search]);

  return (
    <div>
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls={contentId}
        onClick={() => setCollapsed((prev) => !prev)}
        className={`relative w-full px-3 py-2 rounded-md text-sm border flex items-center justify-center gap-2 transition ${collapsed ? "border-white bg-gray-900 text-white hover:bg-white hover:text-gray-900" : "border-blue-600 bg-blue-700 text-white hover:bg-blue-800"}`}
      >
        <span>{title}</span>
        <span aria-hidden="true" className="absolute right-3">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div id={contentId} className="mt-2 ml-3 overflow-hidden rounded-md border border-blue-800 bg-blue-950">
          <div className="px-3 py-2 border-b border-blue-900">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={placeholder}
              aria-label={placeholder}
              className={SEEC_SELECTOR_INPUT_CLASS}
            />
          </div>

          <div className={`${maxListHeightClass} overflow-y-auto`}>
            {filteredItems.map((item) => {
              const isSelected = item === selectedItem;
              return (
                <button
                  key={item}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => onSelectItem(item)}
                  className={`${SEEC_SELECTOR_ROW_CLASS} ${
                    isSelected
                      ? "bg-white text-blue-950 font-semibold"
                      : "bg-blue-950 text-white hover:bg-blue-900"
                  }`}
                >
                  {item}
                </button>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-300">{emptyMessage}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SideBar({
  currentUser = null,
  onNavigate = () => {},

  // Desktop-only tools
  showInterestRatesButton = false,
  onOpenInterestRates = () => {},

  showBuildBudgetButton = false,
  onOpenBuildBudget = () => {},
  showCrimeNavigation = false,
  showSeecTownSelector = false,
  seecPoliticians = [],
  selectedSeecPolitician = "",
  onSelectSeecPolitician = () => {},
  seecPoliticalCommittees = [],
  selectedSeecPoliticalCommittee = "",
  onSelectSeecPoliticalCommittee = () => {},
  seecTowns = [],
  selectedSeecTown = "",
  onSelectSeecTown = () => {},
  seecCandidates = [],
  selectedSeecCandidate = "",
  onSelectSeecCandidate = () => {},
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isElectionsRoute = location.pathname === "/elections";
  const isPropertiesRoute = /^\/properties(\/|$)/.test(location.pathname);
  const propertyNavigation=placement=><AreaNavigation area="properties" placement={placement} onNavigate={onNavigate}/>;
  const electionNavigation=placement=><AreaNavigation area="elections" placement={placement} onNavigate={onNavigate}/>;
  const budgetContext = location.pathname === "/budget-vs-spent"
    ? (new URLSearchParams(location.search).get('context') || location.state?.budgetContext || "town")
    : (/^\/(reports|boe|budget\/(board-of-education|boe))(\/|$)/.test(location.pathname) ? "boe" : "town");
  const showTownNavigation = budgetContext === "town" && /^\/(budget|town)(?:\/|$|-vs-spent)/.test(location.pathname);
  const showMapsNavigation = isMapsRoute(location.pathname);
  const showBudgetComparison = showTownNavigation || /^\/(budget(?:\/|$)|reports(?:\/|$)|boe(?:\/|$)|budget-vs-spent(?:\/|$))/.test(location.pathname);
  const isComparisonRoute = location.pathname === "/budget-vs-spent";
  const [comparisonExpanded, setComparisonExpanded] = useState(isComparisonRoute);
  useEffect(() => { setComparisonExpanded(isComparisonRoute); }, [isComparisonRoute, budgetContext]);
  const comparisonAccordion = placement => (
    <div className="w-full">
      <button type="button" aria-expanded={comparisonExpanded} aria-controls={`comparison-reports-${placement}`}
        onClick={() => {
          if (!isComparisonRoute) {
            setComparisonExpanded(true);
            navigate(`/budget-vs-spent?context=${budgetContext}`);
          } else setComparisonExpanded(open => !open);
        }}
        className="relative w-full px-3 py-2 rounded-md text-sm border border-white flex items-center justify-center gap-2 text-white hover:bg-white hover:text-gray-900">
        Budget vs Spent
        <span aria-hidden="true" className="absolute right-3">{comparisonExpanded ? "▾" : "▸"}</span>
      </button>
      <div id={`comparison-reports-${placement}`} hidden={!comparisonExpanded} className="mt-2">
        {isComparisonRoute && <ReportLinks onNavigate={onNavigate} accordion />}
      </div>
    </div>
  );
  const isBoeRoute = /^\/(reports|boe|budget\/(board-of-education|boe))(\/|$)/.test(location.pathname);
  const isTownBudgetRoute = /^\/(budget|town)\/?$/.test(location.pathname);
  const boeNavigation=placement=><AreaNavigation area="boe" placement={placement} onNavigate={onNavigate}/>;
  const townAccordion=placement=><AreaNavigation area="town" placement={placement} onNavigate={onNavigate}/>;
  const mapsNavigation=placement=><AreaNavigation area="maps" placement={placement} onNavigate={onNavigate}/>;

  const mobileNavClass = ({ isActive }) =>
    `w-full text-left px-3 py-2 rounded-md text-sm border ${
      isActive
        ? "bg-white text-gray-900 border-white font-semibold"
        : "bg-gray-900 text-white border-gray-700 hover:bg-gray-800"
    }`;

  const handleMobileNavClick = () => {
    onNavigate(); // closes sidebar in MainLayout
  };

  const handleSelectSeecTown = (town) => {
    onSelectSeecTown(town);
    navigate("/seec");
    onNavigate();
  };

  const handleSelectSeecPolitician = (politician) => {
    onSelectSeecPolitician(politician);
    navigate("/seec");
    onNavigate();
  };

  const handleSelectSeecCandidate = (candidate) => {
    onSelectSeecCandidate(candidate);
    navigate("/seec");
    onNavigate();
  };

  const handleSelectSeecPoliticalCommittee = (committee) => {
    onSelectSeecPoliticalCommittee(committee);
    navigate("/seec");
    onNavigate();
  };

  return (
    <SidebarLayout>
      {/* =========================
          MOBILE (hamburger panel)
          - ONLY route links
          - NO logo
          - NO tools
         ========================= */}
      <div className="xl:hidden flex flex-col gap-2 px-4 pt-4">
        <NavLink
          to="/budget"
          className={mobileNavClass}
          onClick={handleMobileNavClick}
        >
          Town of Glastonbury
        </NavLink>



        <NavLink
          to="/reports"
          className={mobileNavClass}
          onClick={handleMobileNavClick}
        >
          Board of Education
        </NavLink>

        <Link to="/maps" aria-current={showMapsNavigation ? "page" : undefined}
          className={mobileNavClass({ isActive: showMapsNavigation })} onClick={handleMobileNavClick}>Maps</Link>
        <NavLink to="/elections" className={mobileNavClass} onClick={handleMobileNavClick}>Elections</NavLink>
        {isElectionsRoute && electionNavigation("mobile")}
        <NavLink to="/properties" className={mobileNavClass} onClick={handleMobileNavClick}>Properties</NavLink>
        {isPropertiesRoute && propertyNavigation("mobile")}
        {(isBoeRoute || (isComparisonRoute && budgetContext === "boe")) && boeNavigation("mobile")}
        {(isTownBudgetRoute || (isComparisonRoute && budgetContext === "town")) && townAccordion("mobile")}

        {showBudgetComparison && comparisonAccordion("mobile")}

        {showMapsNavigation && mapsNavigation("mobile")}



      </div>

      {/* =========================
          DESKTOP (always visible)
          - Logo + tools
          - NO route links
         ========================= */}
      <div className="hidden xl:flex xl:flex-col xl:gap-3 xl:px-4 xl:pt-4">
        <Link to="/" aria-label="Glastonbury Data home" onClick={onNavigate} className="flex justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-300">
          <img
            src={glastonburyLogo}
            alt="Glastonbury Logo"
            className="h-32 w-full max-w-[208px] object-contain"
          />
        </Link>

        {isPropertiesRoute && propertyNavigation("desktop")}
        {isElectionsRoute && electionNavigation("desktop")}

        {showBudgetComparison && comparisonAccordion("desktop")}

        {showMapsNavigation && mapsNavigation("desktop")}


        {showInterestRatesButton && (
          <button
            type="button"
            onClick={onOpenInterestRates}
            className="w-full px-3 py-2 rounded-md text-sm border flex items-center justify-center gap-2
                       text-white border-white hover:bg-white hover:text-gray-900"
            title="View interest / inflation rates by year"
          >
            Inflation Rates
          </button>
        )}

        {showBuildBudgetButton && (
          <button
            type="button"
            onClick={onOpenBuildBudget}
            className="w-full px-3 py-2 rounded-md text-sm border flex items-center justify-center gap-2
                       text-white border-white hover:bg-white hover:text-gray-900"
            title="Open the Build a Budget workshop"
          >
            Build a Budget
          </button>
        )}



        {(isBoeRoute || (isComparisonRoute && budgetContext === "boe")) && boeNavigation("desktop")}
        {(isTownBudgetRoute || (isComparisonRoute && budgetContext === "town")) && townAccordion("desktop")}

      </div>
        {showSeecTownSelector && (
          <nav aria-label="SEEC sections" className="flex flex-col gap-3 px-4 pb-4">
            <NavLink to="/seec" end onClick={onNavigate}
              className={({ isActive }) => `w-full px-3 py-2 rounded-md text-sm border flex items-center justify-center ${isActive ? "bg-white text-gray-900 border-white font-semibold" : "bg-gray-900 text-white border-white hover:bg-white hover:text-gray-900"}`}>
              Explore Filings
            </NavLink>
            {location.pathname === "/seec" && <ReportLinks onNavigate={onNavigate} accordion />}
            <NavLink
              to="/seec/top-donors"
              onClick={onNavigate}
              className={({ isActive }) => `w-full px-3 py-2 rounded-md text-sm border flex items-center justify-center ${isActive ? "bg-white text-gray-900 border-white font-semibold" : "bg-gray-900 text-white border-white hover:bg-white hover:text-gray-900"}`}
            >
              Top Donors
            </NavLink>

            <div className="flex flex-col gap-3">
              <SeecSelectorSection
                title="Politician"
                placeholder="Search politicians"
                items={seecPoliticians}
                selectedItem={selectedSeecPolitician}
                onSelectItem={handleSelectSeecPolitician}
                emptyMessage="No politicians found."
                maxListHeightClass="max-h-[18rem]"
              />

              <SeecSelectorSection
                title="Town"
                placeholder="Search towns"
                items={seecTowns}
                selectedItem={selectedSeecTown}
                onSelectItem={handleSelectSeecTown}
                emptyMessage="No towns found."
                maxListHeightClass="max-h-[18rem]"
              />

              <SeecSelectorSection
                title="Political"
                placeholder="Search political groups"
                items={seecPoliticalCommittees}
                selectedItem={selectedSeecPoliticalCommittee}
                onSelectItem={handleSelectSeecPoliticalCommittee}
                emptyMessage="No political groups found."
                maxListHeightClass="max-h-[18rem]"
              />

              <SeecSelectorSection
                title="Candidate"
                placeholder="Search candidates"
                items={seecCandidates}
                selectedItem={selectedSeecCandidate}
                onSelectItem={handleSelectSeecCandidate}
                emptyMessage="No candidates found."
                maxListHeightClass="max-h-[18rem]"
              />
            </div>
          </nav>
        )}
    </SidebarLayout>
  );
}

export default SideBar;
